import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Microscope, UploadCloud, Loader2, Trash2, Clock, CheckCircle2, AlertCircle, Image as ImageIcon, Brain,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const UPLOAD_ROLES = ["technicien", "chef_service", "pathologiste", "pathologiste_distant", "directeur_medical"];
const STATUT = {
  en_traitement: { label: "En traitement", icon: Clock, cls: "text-amber-600 bg-amber-50" },
  pret: { label: "Prête", icon: CheckCircle2, cls: "text-teal-600 bg-teal-50" },
  erreur: { label: "Erreur", icon: AlertCircle, cls: "text-red-600 bg-red-50" },
};

export default function WSI() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUpload = UPLOAD_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // {name, pct}
  const [meta, setMeta] = useState({ coloration: "H&E", type_examen: "", description: "" });
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/wsi"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rafraîchit tant qu'une lame est en traitement
  useEffect(() => {
    const anyProcessing = items.some((i) => i.statut === "en_traitement");
    if (anyProcessing && !pollRef.current) {
      pollRef.current = setInterval(load, 3000);
    } else if (!anyProcessing && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !items.some((i) => i.statut === "en_traitement")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, load]);

  const upload = async (file) => {
    setUploading({ name: file.name, pct: 0 });
    try {
      const { data: init } = await api.post("/wsi/upload/init", {
        filename: file.name, filesize: file.size,
        coloration: meta.coloration, type_examen: meta.type_examen, description: meta.description,
      });
      const CH = init.chunk_size;
      const total = Math.ceil(file.size / CH) || 1;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CH, (i + 1) * CH);
        const fd = new FormData();
        fd.append("chunk", blob, "part");
        await api.post(`/wsi/upload/chunk?upload_id=${init.upload_id}&index=${i}`, fd);
        setUploading({ name: file.name, pct: Math.round(((i + 1) / total) * 100) });
      }
      await api.post("/wsi/upload/complete", { upload_id: init.upload_id, total_chunks: total });
      toast.success("Lame téléversée — génération des tuiles en cours");
      setUploading(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setUploading(null);
    }
  };

  const onPick = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette lame et ses tuiles ?")) return;
    try { await api.delete(`/wsi/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success("Lame supprimée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="wsi-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Microscope className="h-6 w-6 text-sky-600" /> Lames virtuelles (WSI)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visionneuse haute résolution · tuilage Deep Zoom · annotations et signature numérique
          </p>
        </div>
        {canUpload && (
          <button onClick={() => fileRef.current?.click()} disabled={!!uploading}
                  data-testid="wsi-upload-btn"
                  className="flex items-center gap-2 bg-sky-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-sky-700 disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? `${uploading.pct}%` : "Téléverser une lame"}
          </button>
        )}
        <input ref={fileRef} type="file" hidden onChange={onPick}
               accept=".tif,.tiff,.svs,.ndpi,.png,.jpg,.jpeg" data-testid="wsi-file-input" />
      </div>

      {canUpload && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <div>
            <label className="text-xs font-medium text-slate-500">Coloration</label>
            <input value={meta.coloration} onChange={(e) => setMeta({ ...meta, coloration: e.target.value })}
                   data-testid="wsi-meta-coloration"
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Type d'examen</label>
            <input value={meta.type_examen} onChange={(e) => setMeta({ ...meta, type_examen: e.target.value })}
                   placeholder="Biopsie, pièce…" data-testid="wsi-meta-type"
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Description</label>
            <input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                   data-testid="wsi-meta-desc"
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
        </div>
      )}

      {uploading && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
          <div className="flex items-center justify-between text-sm text-sky-800 mb-1.5">
            <span className="truncate">{uploading.name}</span><span>{uploading.pct}%</span>
          </div>
          <div className="h-2 bg-sky-100 rounded-full overflow-hidden">
            <div className="h-full bg-sky-600 transition-all" style={{ width: `${uploading.pct}%` }} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="wsi-empty">
          <Microscope className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Aucune lame virtuelle pour l'instant</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="wsi-grid">
          {items.map((w) => {
            const st = STATUT[w.statut] || STATUT.en_traitement;
            const Icon = st.icon;
            const ready = w.statut === "pret";
            return (
              <div key={w.id} onClick={() => ready && navigate(`/wsi/${w.id}`)}
                   data-testid={`wsi-card-${w.id}`}
                   className={`bg-white border border-slate-200 rounded-xl overflow-hidden group ${ready ? "cursor-pointer hover:shadow-md hover:border-sky-300" : "opacity-80"} transition-all`}>
                <div className="aspect-[4/3] bg-slate-100 relative flex items-center justify-center overflow-hidden">
                  {ready ? (
                    <img src={`${API}/wsi/${w.id}/thumbnail.jpg`} alt={w.nom}
                         className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-slate-300" />
                  )}
                  <span className={`absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${st.cls}`}>
                    <Icon className="h-3 w-3" /> {st.label}
                  </span>
                  {w.ia_disponible && (
                    <span data-testid={`wsi-ia-badge-${w.id}`}
                          className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 text-white ${w.ia_score >= 0.7 ? "bg-red-600" : w.ia_score >= 0.4 ? "bg-amber-500" : "bg-teal-600"}`}>
                      <Brain className="h-3 w-3" /> IA {Math.round(w.ia_score * 100)}%
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{w.nom}</p>
                      <p className="text-xs text-slate-400 truncate">
                        {w.coloration || "—"}{w.type_examen ? ` · ${w.type_examen}` : ""}
                      </p>
                    </div>
                    {canUpload && (
                      <button onClick={(e) => remove(w.id, e)} data-testid={`wsi-delete-${w.id}`}
                              className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
